import type { StateMachine } from './machine';

/** Payment state machine — core-data-model §3.5. Stripe-side transitions are webhook-driven
 *  system mutations (idempotent on Stripe event id). Manual cash/check payments are inserted
 *  directly at `succeeded` (the machine defines legal TRANSITIONS; the insert sets the state). */
export const PAYMENT_STATES = [
  'pending',
  'processing',
  'succeeded',
  'failed',
  'canceled',
  'partially_refunded',
  'refunded',
  'disputed',
] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

export const paymentMachine: StateMachine<PaymentState> = {
  states: PAYMENT_STATES,
  initial: 'pending',
  terminal: ['failed', 'canceled', 'refunded'],
  transitions: [
    { from: 'pending', to: 'processing', via: 'payment.processing', system: true }, // ACH
    { from: 'pending', to: 'succeeded', via: 'payment.succeeded', system: true }, // card instant
    { from: 'pending', to: 'failed', via: 'payment.failed', system: true },
    { from: 'pending', to: 'canceled', via: 'payment.cancel' },
    { from: 'processing', to: 'succeeded', via: 'payment.succeeded', system: true },
    { from: 'processing', to: 'failed', via: 'payment.failed', system: true }, // ACH return
    { from: 'succeeded', to: 'partially_refunded', via: 'refund.partial' },
    // Giving back the rest in more than one go. Without this edge, a shop that refunded
    // $6,000 of $10,000 and then tried to refund $2,000 more got `Illegal payment
    // transition partially_refunded → (none) via refund.partial` — and the only way
    // through was to refund the FULL amount again, which the bound then allowed.
    { from: 'partially_refunded', to: 'partially_refunded', via: 'refund.partial' },
    { from: 'partially_refunded', to: 'refunded', via: 'refund.full' },
    { from: 'succeeded', to: 'refunded', via: 'refund.full' },
    { from: 'succeeded', to: 'disputed', via: 'payment.disputed', system: true },
    { from: 'disputed', to: 'succeeded', via: 'dispute.won', system: true },
    { from: 'disputed', to: 'refunded', via: 'dispute.lost', system: true },
  ],
};

export const PAYMENT_METHODS = ['card', 'ach', 'cash', 'check', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
